import { Group, Paper } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconMessage, IconPackage, IconThumbDown, IconThumbUp } from '@tabler/icons-react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import type { MRT_ColumnFiltersState, MRT_PaginationState, MRT_SortingState } from 'mantine-react-table'
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table'
import { useState } from 'react'

import { StatInfoGraphic } from '@/components/StatInfoGraphic'
import { useGlobalState } from '@/hooks/useGlobalState'
import { useTopicsEnabled } from '@/hooks/useTopicsEnabled'
import type { FeedbackAnnotation } from '@/services/api/api.types'
import * as classes from '@/styles/Table.css'

import { useFeedbackTableColumns } from './columns'
import { FeedbackDetailModal } from './FeedbackDetailModal'
import { useFeedbackFilters } from './useFeedbackFilters'

export function FeedbackTable() {
  const { api } = useGlobalState()
  const { topicsEnabled } = useTopicsEnabled()

  const [sorting, setSorting] = useState<MRT_SortingState>([])
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackAnnotation | null>(null)
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false)

  const topicsQuery = useQuery({
    queryKey: ['sponsor', 'topics'],
    queryFn: () => api.getSponsorTopics(),
    staleTime: 5 * 60 * 1000,
  })

  const tagsQuery = useQuery({
    queryKey: ['feedback', 'tags'],
    queryFn: () => api.getFeedbackTagList(),
    staleTime: 5 * 60 * 1000,
  })

  const filters = useFeedbackFilters({ columnFilters, sorting })

  const { data, isLoading } = useQuery({
    queryKey: ['feedback', 'list', pagination, filters],
    queryFn: () =>
      api.getFeedback({
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        ...filters,
      }),
    placeholderData: keepPreviousData,
  })

  const onPaginationChange = (updater: MRT_PaginationState | ((old: MRT_PaginationState) => MRT_PaginationState)) => {
    setPagination(updater)
  }

  const handleViewDetails = (feedback: FeedbackAnnotation) => {
    setSelectedFeedback(feedback)
    openModal()
  }

  const columns = useFeedbackTableColumns({
    topicsData: topicsQuery.data || [],
    contentModulesData: data?.stats?.byContentModule || {},
    byTopicData: data?.stats?.byTopic || {},
    tagsData: tagsQuery.data || [],
    onViewDetails: handleViewDetails,
    topicsEnabled,
  })

  const table = useMantineReactTable({
    columns,
    data: data?.results ?? [],
    enableColumnFilters: true,
    enableGlobalFilter: false,
    enableSorting: true,
    enablePagination: true,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onPaginationChange: onPaginationChange,
    rowCount: data?.total ?? 0,
    pageCount: data?.totalPages ?? 0,
    state: {
      pagination,
      sorting,
      columnFilters,
      isLoading,
    },
    mantinePaperProps: {
      shadow: 'sm',
      radius: 'md',
      p: 'lg',
      className: classes.paginationCustomWidth,
    },
  })

  return (
    <>
      {/* Aggregated Stats */}
      {data?.stats && (
        <Paper p="lg" mb="md" shadow="sm" radius="md" px="xl">
          <Group gap="xl" justify="flex-start" align="top">
            <StatInfoGraphic
              icon={IconPackage}
              label="Modules"
              value={data.stats.modules}
              tooltipText="Number of modules with feedback"
            />
            <StatInfoGraphic
              icon={IconMessage}
              label="Comments"
              value={data.stats.comments}
              tooltipText="Total number of feedback comments"
            />
            <StatInfoGraphic
              icon={IconThumbUp}
              label="Thumbs Up"
              value={data.stats.thumbsUp}
              tooltipText="Number of positive feedback"
            />
            <StatInfoGraphic
              icon={IconThumbDown}
              label="Thumbs Down"
              value={data.stats.thumbsDown}
              tooltipText="Number of negative feedback"
            />
          </Group>
        </Paper>
      )}

      <MantineReactTable table={table} />

      <FeedbackDetailModal
        opened={modalOpened}
        onClose={closeModal}
        feedback={selectedFeedback}
        topicsData={topicsQuery.data || []}
      />
    </>
  )
}
